/**
 * TrameAutoEval.jsx — Mini auto-évaluation élève en fin de phase.
 *
 * S'affiche en mode ÉLÈVE, à la fin de la liste des compétences.
 * Pour chaque compétence, 3 boutons :
 *   - « Je maîtrise »       (état 'ok')
 *   - « À retravailler »    (état 'todo')
 *   - « Pas encore »        (état 'no')
 *
 * Persistance : useLivretSync avec clé dédiée par trame + phase.
 * → Guest : localStorage (offline OK)
 * → Connecté : sync MongoDB automatique + merge non destructif.
 *
 * Réutilise 100% de l'infrastructure existante (pas de nouveau endpoint,
 * pas de nouveau composant de persistance).
 */
import React from 'react';
import {
  CheckCircle2, RotateCcw, AlertCircle, Cloud, Sparkles,
} from 'lucide-react';
import { useApp } from '../../contexts/AppContext';

const STATES = [
  { value: 'ok',   label: 'Je maîtrise',    short: '✅', icon: CheckCircle2, tone: 'emerald' },
  { value: 'todo', label: 'À retravailler', short: '🔁', icon: RotateCcw,    tone: 'amber' },
  { value: 'no',   label: 'Pas encore',     short: '⏳', icon: AlertCircle,  tone: 'rose' },
];

const TONE_CLASSES = {
  emerald: {
    active: 'bg-emerald-500/25 border-emerald-400/60 text-emerald-100',
    idle:   'bg-white/[0.03] border-white/10 text-white/50 hover:bg-emerald-500/10 hover:border-emerald-500/30 hover:text-emerald-200',
  },
  amber: {
    active: 'bg-amber-500/25 border-amber-400/60 text-amber-100',
    idle:   'bg-white/[0.03] border-white/10 text-white/50 hover:bg-amber-500/10 hover:border-amber-500/30 hover:text-amber-200',
  },
  rose: {
    active: 'bg-rose-500/25 border-rose-400/60 text-rose-100',
    idle:   'bg-white/[0.03] border-white/10 text-white/50 hover:bg-rose-500/10 hover:border-rose-500/30 hover:text-rose-200',
  },
};

export default function TrameAutoEval({ phase, color = '#e11d48', state, setValue, syncing }) {
  const { user } = useApp();

  const items = (phase.exercises || []);
  if (!items.length) return null;

  const doneCount = items.filter(ex => state[ex.id] === 'ok').length;
  const total = items.length;
  const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;

  return (
    <section
      className="mt-6 rounded-2xl border overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${color}12 0%, transparent 70%)`,
        borderColor: `${color}55`,
      }}
      data-testid={`autoeval-${phase.id}`}
    >
      <div className="p-5 sm:p-6">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border"
            style={{ color, borderColor: `${color}55`, background: `${color}15` }}
          >
            <Sparkles className="w-3 h-3" /> Auto-évaluation · {phase.code || phase.id}
          </span>
          {user && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[9px] font-black uppercase tracking-widest" title={syncing ? 'Sync…' : 'Sauvegardé cloud'}>
              <Cloud className="w-2.5 h-2.5" /> {syncing ? 'Sync…' : 'Cloud'}
            </span>
          )}
        </div>

        <h3
          className="text-white font-black text-lg sm:text-xl leading-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Où en es-tu sur cette session ?
        </h3>
        <p className="text-white/70 text-[12.5px] mt-1 leading-relaxed">
          Tape sur ✅ · 🔁 · ⏳ à côté de chaque compétence. Sois honnête, c'est pour toi.
        </p>

        {/* Progression */}
        <div className="mt-3 mb-4">
          <div className="flex items-center justify-between text-[10.5px] font-black uppercase tracking-widest text-white/60 mb-1.5">
            <span>Compétences maîtrisées</span>
            <span style={{ color }}>{doneCount} / {total} · {pct} %</span>
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
          </div>
        </div>

        {/* Légende compacte — visible une fois pour clarifier les 3 emojis */}
        <div className="mb-3 flex flex-wrap gap-x-3 gap-y-1 text-[10.5px] text-white/55">
          {STATES.map(s => (
            <span key={s.value} className="inline-flex items-center gap-1">
              <span className="text-[13px] leading-none">{s.short}</span> {s.label}
            </span>
          ))}
        </div>

        {/* Liste compétences — layout compact 1 ligne par compétence */}
        <ul className="space-y-1.5">
          {items.map(ex => {
            const current = state[ex.id];
            return (
              <li
                key={ex.id}
                className="rounded-xl bg-black/30 border border-white/10 px-3 py-2 flex items-center gap-2"
                data-testid={`autoeval-item-${ex.id}`}
              >
                <div className="text-white text-[13px] leading-tight flex-1 min-w-0 pr-1">
                  {ex.title}
                </div>
                <div className="inline-flex gap-1 flex-shrink-0" role="radiogroup" aria-label={`Auto-évaluation : ${ex.title}`}>
                  {STATES.map(opt => {
                    const active = current === opt.value;
                    const cls = TONE_CLASSES[opt.tone][active ? 'active' : 'idle'];
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setValue(ex.id, active ? null : opt.value)}
                        className={`w-9 h-9 rounded-lg border text-[16px] leading-none flex items-center justify-center transition ${cls}`}
                        data-testid={`autoeval-${ex.id}-${opt.value}`}
                        aria-pressed={active}
                        aria-label={opt.label}
                        title={opt.label}
                      >
                        {opt.short}
                      </button>
                    );
                  })}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
